import { Cookies } from 'react-cookie';
import { Logger } from '@saviynt/common';
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
} from 'axios';

import { AXIOS_TIMEOUT_ERROR_MESSAGE } from '../constants';
import { checkEnvironment } from '../utils';

const cookies = new Cookies();

/**
 * Pre-configured Axios instance with request and response interceptors.
 * Automatically attaches bearer tokens from cookies, sets the base URL
 * from the environment, strips the default Content-Type for FormData
 * payloads, and handles 401/session-expiry redirects.
 */
const axiosService: AxiosInstance = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor for adding the bearer token dynamically
axiosService.interceptors.request.use(
  (config) => {
    const { ECM_URL } = checkEnvironment();

    config.baseURL = ECM_URL;
    // Token is added from cookies dynamically for each request
    const token: string | undefined = cookies.get<string>('access_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Let the browser set the correct Content-Type (multipart/form-data with
    // boundary) when sending FormData — the default 'application/json' header
    // would prevent files from being transmitted.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor to handle 401 errors
axiosService.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    const { responseURL } = response.request as { responseURL?: string };

    if (responseURL) {
      try {
        // Info: Redirecting to login page when session expires
        if (responseURL.includes('/login/auth')) {
          window.location.reload();
        }
      } catch (e) {
        Logger.log('Redirecting error', e);
      }
    }

    return response;
  },
  (error: AxiosError) => {
    // If the error status is 401, redirect to the login page
    if (error.response?.status === 401) {
      window.location.reload();
    }

    if (error.code === AxiosError.ECONNABORTED) {
      return Promise.reject(AXIOS_TIMEOUT_ERROR_MESSAGE);
    }

    return Promise.reject(error);
  }
);

export default axiosService;
